import { db } from "../../boot/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { call } from "../../util/misc";
import Log from "../../classes/Log";

export { LISTEN_DOC, LISTEN_COLLECTION, UNLISTEN } from "../main/actions/init";

export const GET_LOG = async ({ commit, state }, id) => {
  if (id in state.logs) {
    return state.logs[id];
  }
  let snapshot = await getDoc(doc(db, Log.getPath(id)).withConverter(Log));
  if (!snapshot.exists()) {
    throw "Not found";
  }
  const log = snapshot.data();
  commit("SET", { stateKey: "logs", doc: log });
  return log;
};

// Not exported: Vuex registers every export of an actions module as an action,
// so a non-function export here fails store construction with
// `actions should be function or object with "handler" function`.
const LOGS_PAGE_LIMIT = 50;

/**
 * One page of the audit trail, newest first.
 *
 * This replaces LISTEN_LOGS, which subscribed to the whole `logs` collection —
 * every one of the ~18k documents streamed into the browser — and then filtered
 * client-side. That was what capped retention at 14 days.
 *
 * `doc` matches a document or any of its ancestors, so one value scopes the
 * search to a whole subtree: a project's ID finds the project and every sale,
 * item, order and invoice under it. It takes either a bare ID or an ID chain —
 * `04400003/B` for one project's sale B, since every project numbers its sales
 * `A`, `B`, `C` and a bare `B` therefore means "sale B of any project". See
 * getPathDocs in functions/logs.js for how both forms are indexed.
 *
 * It deliberately does not combine with the other filters. Firestore needs a
 * composite index per combination of filters, and a document ID is selective
 * enough on its own that combining it would only add index shapes for no
 * practical gain. When it is set, it wins.
 *
 * Results are also committed to `state.admin.logs` so that LogView's
 * open-by-id path stays a state hit rather than a fetch. The page keeps its own
 * ordered array: this state is an unordered map and accumulates across filter
 * changes, so it cannot be rendered directly.
 */
export const GET_LOGS_PAGE = async (
  { commit },
  {
    from = null,
    to = null,
    uid = null,
    event = null,
    collection: logCollection = null,
    collections = null,
    doc: logDoc = null,
    limitCount = LOGS_PAGE_LIMIT,
    startAfterDoc = null,
  } = {}
) => {
  const constraints = [];

  if (logDoc) {
    // Matched verbatim: `pathDocs` indexes every form the value could be typed
    // in — see getPathDocs in functions/logs.js — so this only has to forgive
    // the slashes around a pasted path.
    const value = logDoc.trim().replace(/^\/+|\/+$/g, "");
    constraints.push(where("pathDocs", "array-contains", value));
  } else {
    if (uid) constraints.push(where("uid", "==", uid));
    if (event) constraints.push(where("event", "==", event));
    // A multi-value collection filter becomes an `in`, which Firestore serves
    // from the same composite index as `==`, so the multi-select costs no extra
    // index. `in` is capped at 30 values.
    if (collections && collections.length) {
      constraints.push(where("collection", "in", collections.slice(0, 30)));
    } else if (logCollection) {
      constraints.push(where("collection", "==", logCollection));
    }
  }

  // `timestamp` is stored as epoch milliseconds — functions/logs.js writes
  // `new Date().getTime()`, not a Firestore Timestamp. Firestore orders values
  // by type before value, and numbers sort before timestamps, so a range bound
  // handed in as a Date matches no document at all. The page thinks in dates,
  // so coerce here, where the stored shape is known.
  if (from) constraints.push(where("timestamp", ">=", Number(from)));
  if (to) constraints.push(where("timestamp", "<=", Number(to)));
  constraints.push(orderBy("timestamp", "desc"));
  constraints.push(limit(limitCount));
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc));

  const snapshot = await getDocs(
    query(collection(db, Log.getPath()).withConverter(Log), ...constraints)
  );
  const logs = snapshot.docs.map((snap) => snap.data());

  commit("SET_MANY", {
    stateKey: "logs",
    docs: logs.map((log) => ({ doc: log })),
  });

  return {
    logs,
    // Whoever holds this cursor must keep it out of Vue's reactivity — see
    // Logs.vue's `load()`, which markRaws it. Reactive-wrapped, its internals
    // come back out of `startAfter()` as Proxies inside the query's cursor
    // position, and Firestore's IndexedDB cache cannot structured-clone a Proxy
    // when it persists the target.
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    // Short page means the cursor has reached the end of the range.
    done: snapshot.size < limitCount,
  };
};

export const RESTORE = (context, logID) => {
  return call("restore", logID);
};
